import clsx from 'clsx';
import React from 'react';

import { Button } from '../Button';
import { ButtonGroup } from '../ButtonGroup';
import { Icon } from '../Icon';
import { Image } from '../Image';
import { QuoteForm } from '../QuoteForm';
import { Rating } from '../Rating';
import { Video } from '../Video';

import type { ButtonProps } from '../Button/Button.types';
import type { CTAProps } from '../CTA/CTA.types';
import type { BillboardCTAs, BillboardProps } from './Billboard.types';

export const Billboard = ({
  backgroundCentered,
  backgroundInverse,
  categoryText,
  ctas,
  customGradient,
  image,
  imageAlign = 'right',
  imageAsBackground,
  inverseVariant,
  video,
  isBasic,
  leadText,
  mainHeadingText,
  moreButtonHrefId,
  rating,
  quoteForm,
  testId,
}: BillboardProps): JSX.Element => {
  const cssProperties = {};

  if (customGradient) {
    cssProperties['--billboard-gradient-angle'] = customGradient.angle;
    cssProperties['--billboard-gradient-1-stop'] = customGradient.gradient1stop;
    cssProperties['--billboard-gradient-1-color'] =
      customGradient.gradient1color;
    cssProperties['--billboard-gradient-2-stop'] = customGradient.gradient2stop;
    cssProperties['--billboard-gradient-2-color'] =
      customGradient.gradient2color;
  }

  function isButton(_ctas: BillboardCTAs): _ctas is ButtonProps {
    return _ctas && ('onClick' in _ctas || 'href' in _ctas);
  }

  function isButtonArr(_ctas: BillboardCTAs): _ctas is ButtonProps[] {
    return _ctas && 'map' in _ctas;
  }

  function isCTA(_ctas: BillboardCTAs): _ctas is CTAProps {
    return _ctas && 'buttons' in _ctas;
  }

  const onMoreClick = (event) => {
    event.preventDefault();
    const element = document.getElementById(moreButtonHrefId);
    window.scroll({ top: element.offsetTop, left: 0, behavior: 'smooth' });
  };

  return (
    <div
      className={clsx('billboard', {
        billboard_basic: isBasic,
        billboard_text: !image && !video,
        billboard_background: image && imageAsBackground,
        'billboard_background-inverse': backgroundInverse && imageAsBackground,
        'billboard_background-inverse_variant':
          backgroundInverse && imageAsBackground && inverseVariant,
        'billboard_background-center_image': backgroundCentered,
      })}
      data-testid={testId}
    >
      {/* Billboard Background Image */}
      {image && imageAsBackground && (
        <div
          className={clsx('billboard__media', 'media', {
            'billboard__media_align-left': imageAlign === 'left',
          })}
          style={
            backgroundInverse &&
            imageAsBackground &&
            inverseVariant &&
            customGradient
              ? cssProperties
              : null
          }
        >
          <Image
            img={image.img}
            classNames='billboard__image'
            pictureClassNames='picture'
          />
        </div>
      )}

      <div className='billboard__content'>
        {/* Category Text */}
        {categoryText && (
          <div className='billboard__category text-heading_5'>
            {categoryText}
          </div>
        )}

        {/* Main Text */}
        {mainHeadingText && (
          <div className={clsx('billboard__heading', 'text-display_1')}>
            {mainHeadingText}
          </div>
        )}

        {/* Sub Text */}
        {leadText && <p className='billboard__lead'>{leadText}</p>}

        {/* CTA's */}
        <div className='billboard__cta-wrapper'>
          {/* Quote Form */}
          {quoteForm && <QuoteForm {...quoteForm} />}

          {/* Primary CTA as a Button */}
          {isButton(ctas) && (
            <div
              className={clsx('button-wrapper', {
                'billboard__button-wrapper': quoteForm,
              })}
            >
              {!quoteForm ? (
                <ButtonGroup buttons={[ctas]} />
              ) : (
                <Button {...ctas} />
              )}
            </div>
          )}

          {/* Buttons */}
          {isButtonArr(ctas) && (
            <ButtonGroup
              buttons={ctas}
              classNames={'button-wrapper'}
              itemClassNames={'button-group__item'}
            />
          )}

          {/* Secondary CTA */}
          {isCTA(ctas) && (
            <>
              <div className='divider billboard__cta-divider' />
              <div className='billboard__cta-secondary-wrapper'>
                <div className='billboard__cta-secondary-lead'>
                  {ctas.leadText}
                </div>
                <div
                  className={clsx('billboard__cta-secondary-button-wrapper')}
                >
                  <Button href={ctas.buttons[0].href} buttonType='Secondary'>
                    {ctas.buttons[0].buttonLabel}
                  </Button>
                </div>
              </div>
            </>
          )}
          {rating && (
            <Rating
              rating={rating}
              isBackgroundDark={backgroundInverse}
              isNumericVisible={true}
            />
          )}
        </div>
      </div>

      {/* Billboard Normal Image */}
      {image && !imageAsBackground && (
        <div className='billboard__media media'>
          <Image img={image.img} classNames='billboard__image media__image' />
        </div>
      )}

      {/* Billboard Video */}
      {video && <div className='billboard__media'>{<Video {...video} />}</div>}

      {/* Down Arrow Scroll Button */}
      {image && imageAsBackground && moreButtonHrefId && (
        <div className='billboard__more'>
          <a
            href={`#${moreButtonHrefId}`}
            aria-label='More'
            onClick={onMoreClick}
          >
            <Icon size='xxlarge' icon='More48' color='white' />
          </a>
        </div>
      )}
    </div>
  );
};
